import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { AlertCircle, CalendarCheck, CalendarClock, CalendarX, Clock, Loader2 } from 'lucide-react';
import { lookOf, validHandle } from '@/lib/brandSources';
import { TourChrome, TourMessage } from './TourChrome';
import { tourLink, type TourLinkView } from './tourApi';
import { TOUR_STRINGS, dayLabel, timeLabel, tourLangOf, type TourLang, type TourStrings } from './strings';
import { adoptLang, useLang } from '@/lib/lang';

/**
 * /o/<handle>/tour/manage#t=<token>: the link from the tour emails. Confirms a
 * pending booking (a button press, so a mail scanner opening the link never
 * confirms it), shows a booked one with a cancel option, and says plainly when
 * a booking expired, was cancelled or passed. The token lives in the URL
 * fragment, which browsers never send to a server.
 */
function readFragment(hash: string): { token: string | null; lang: TourLang | null } {
  const p = new URLSearchParams(hash.replace(/^#/, ''));
  const token = p.get('t');
  return { token: token && /^[A-Za-z0-9_-]{16,64}$/.test(token) ? token : null, lang: tourLangOf(p.get('l')) };
}

type State =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'error'; message: string }
  | { kind: 'view'; view: TourLinkView };

export function TourManagePage() {
  const handle = validHandle(useParams().handle);
  const { hash } = useLocation();
  const fragment = readFragment(hash);
  // The token of the link as opened; the language switch keeps it (it never touches the URL).
  const [initial] = useState(fragment);
  // The email's language (`#l=ar`) becomes the choice; the switch changes it from there.
  useEffect(() => adoptLang(initial.lang), [initial.lang]);
  const lang = useLang((st) => st.lang);
  const s = TOUR_STRINGS[lang];
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [busy, setBusy] = useState<'confirm' | 'cancel' | null>(null);
  const [asking, setAsking] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    if (!initial.token) return setState({ kind: 'invalid' });
    let live = true;
    void tourLink('view', initial.token, lang).then((r) => {
      if (!live) return;
      if (r.ok) setState({ kind: 'view', view: r.data });
      else if (r.status === 404) setState({ kind: 'invalid' });
      else setState({ kind: 'error', message: s.errors[r.code] ?? s.errors.DEFAULT! });
    });
    return () => {
      live = false;
    };
  }, [initial.token, lang, s]);

  async function act(action: 'confirm' | 'cancel') {
    if (!initial.token) return;
    setBusy(action);
    setActionError('');
    const r = await tourLink(action, initial.token, lang);
    setBusy(null);
    setAsking(false);
    if (r.ok) {
      navigator.vibrate?.([30, 50, 30]);
      return setState({ kind: 'view', view: r.data });
    }
    if (r.code === 'TOUR_EXPIRED' && state.kind === 'view') {
      return setState({ kind: 'view', view: { ...state.view, status: 'expired', canCancel: false } });
    }
    setActionError(s.errors[r.code] ?? s.errors.DEFAULT!);
  }

  const view = state.kind === 'view' ? state.view : null;
  const brand = useMemo(() => lookOf(view?.brand ?? null), [view?.brand]);
  const bookHandle = view?.handle ?? null;

  return (
    <TourChrome brand={brand} loading={state.kind === 'loading'} lang={lang} s={s} title={s.manageTitle}>
      {state.kind === 'loading' && (
        <div className="flex justify-center py-10" aria-busy="true">
          <Loader2 className="h-7 w-7 animate-spin text-brand-500" />
        </div>
      )}
      {state.kind === 'invalid' && (
        <TourMessage icon={<AlertCircle className="h-8 w-8 text-red-500" />} title={s.linkInvalidTitle} tone="error">
          <p className="text-[16px] leading-relaxed text-slate-600">{s.linkInvalidBody}</p>
        </TourMessage>
      )}
      {state.kind === 'error' && (
        <TourMessage icon={<AlertCircle className="h-8 w-8 text-red-500" />} title={state.message} tone="error" />
      )}
      {view && (
        <ManageView
          view={view}
          s={s}
          busy={busy}
          asking={asking}
          setAsking={setAsking}
          error={actionError}
          onConfirm={() => void act('confirm')}
          onCancel={() => void act('cancel')}
          bookHref={bookHandle ? `/o/${bookHandle}/tour${lang !== 'he' ? `?lang=${lang}` : ''}` : null}
        />
      )}
    </TourChrome>
  );
}

function ManageView({
  view,
  s,
  busy,
  asking,
  setAsking,
  error,
  onConfirm,
  onCancel,
  bookHref,
}: {
  view: TourLinkView;
  s: TourStrings;
  busy: 'confirm' | 'cancel' | null;
  asking: boolean;
  setAsking: (v: boolean) => void;
  error: string;
  onConfirm: () => void;
  onCancel: () => void;
  bookHref: string | null;
}) {
  const head = {
    pending: { icon: <CalendarClock className="h-9 w-9 text-brand-500" />, title: s.manageTitle, body: s.pendingBody },
    confirmed: {
      icon: <CalendarCheck className="h-9 w-9 text-done" />,
      title: s.confirmedTitle,
      body: s.confirmedBody,
    },
    cancelled: {
      icon: <CalendarX className="h-9 w-9 text-slate-400" />,
      title: s.cancelledTitle,
      body: s.cancelledBody,
    },
    expired: { icon: <Clock className="h-9 w-9 text-slate-400" />, title: s.expiredTitle, body: s.expiredBody },
    past: { icon: <Clock className="h-9 w-9 text-slate-400" />, title: s.pastTitle, body: s.pastBody },
  }[view.status];
  const struck = view.status === 'cancelled' || view.status === 'expired';

  return (
    <div data-tour-status={view.status}>
      <TourMessage icon={head.icon} title={head.title}>
        <p className="text-[16px] leading-relaxed text-slate-600">{head.body}</p>
        <dl className={'mt-4 rounded-xl bg-slate-50 p-4 text-start text-[16px] ' + (struck ? 'line-through' : '')}>
          <div className="flex flex-wrap gap-x-2">
            <dt className="font-semibold text-slate-500">{s.when}:</dt>
            <dd className="text-slate-800">
              {dayLabel(view.start, view.timeZone, s)} ·{' '}
              <span dir="ltr">{timeLabel(view.start, view.timeZone, s)}</span>
            </dd>
          </div>
          {view.unitTypeName && (
            <div className="mt-1 flex flex-wrap gap-x-2">
              <dt className="font-semibold text-slate-500">{s.what}:</dt>
              <dd className="text-slate-800">{view.unitTypeName}</dd>
            </div>
          )}
        </dl>
      </TourMessage>

      {error && (
        <p className="mt-4 rounded-xl bg-red-50 p-3 text-[16px] leading-relaxed text-red-700" role="alert">
          {error}
        </p>
      )}

      {view.status === 'pending' && (
        <button
          type="button"
          onClick={onConfirm}
          disabled={!!busy}
          className="mt-6 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-4 text-[17px] font-semibold text-white disabled:opacity-60"
        >
          {busy === 'confirm' && <Loader2 className="h-5 w-5 animate-spin" />}
          {busy === 'confirm' ? s.confirming : s.confirm}
        </button>
      )}

      {view.canCancel && !asking && (
        <button
          type="button"
          onClick={() => setAsking(true)}
          disabled={!!busy}
          className="mt-3 min-h-[48px] w-full rounded-xl border border-slate-200 bg-white px-4 text-[16px] font-semibold text-slate-700"
        >
          {s.cancel}
        </button>
      )}
      {view.canCancel && asking && (
        <div className="mt-3 rounded-2xl border border-red-100 bg-red-50 p-4" role="group" aria-label={s.cancelAsk}>
          <p className="mb-3 text-[16px] font-semibold text-red-800">{s.cancelAsk}</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={!!busy}
              className="flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl bg-red-600 px-4 text-[16px] font-semibold text-white disabled:opacity-60"
            >
              {busy === 'cancel' && <Loader2 className="h-5 w-5 animate-spin" />}
              {s.cancelYes}
            </button>
            <button
              type="button"
              onClick={() => setAsking(false)}
              className="min-h-[48px] flex-1 rounded-xl border border-slate-200 bg-white px-4 text-[16px] font-semibold text-slate-700"
            >
              {s.cancelNo}
            </button>
          </div>
        </div>
      )}

      {!view.canCancel && view.status !== 'confirmed' && bookHref && (
        <Link
          to={bookHref}
          className="mt-6 flex min-h-[52px] w-full items-center justify-center rounded-xl bg-brand-600 px-4 text-[17px] font-semibold text-white"
        >
          {s.bookAgain}
        </Link>
      )}
    </div>
  );
}
